import { db, firebaseService, Methodology, DEFAULT_METHODOLOGIES } from './firebase';
import { collection, getDocs, addDoc, doc, getDoc, setDoc, writeBatch, serverTimestamp } from 'firebase/firestore';

const METHODOLOGIES_COLLECTION = 'methodologies';

/**
 * Seeds Firestore with default methodology data if none exists
 * @returns Promise that resolves when seeding is complete
 */
export const seedFirestore = async (): Promise<void> => {
  console.log('Starting Firestore seeding...');
  
  if (!firebaseService.isInitialized || !db) {
    console.warn('Firebase not initialized, skipping seed');
    return;
  }

  try {
    // Check if methodologies already exist
    const methodologiesRef = collection(db, METHODOLOGIES_COLLECTION);
    const snapshot = await getDocs(methodologiesRef);
    
    if (!snapshot.empty) {
      console.log('Firestore already contains methodology data, skipping seed');
      return;
    }

    console.log('Seeding Firestore with default methodologies...');
    
    // Add default methodologies
    for (const methodology of DEFAULT_METHODOLOGIES) {
      try {
        await addDoc(methodologiesRef, methodology);
        console.log(`Added methodology: ${methodology.title}`);
      } catch (error) {
        console.error(`Error adding methodology ${methodology.title}:`, error);
      }
    }

    console.log('Firestore seeding completed successfully');
  } catch (error) {
    console.error('Error seeding Firestore:', error);
  }
};

/**
 * Resets and seeds test data in Firestore for development
 * CAUTION: This will delete existing data
 */
export const resetAndSeedTestData = async (): Promise<boolean> => {
  if (!firebaseService.isInitialized || !db) {
    console.error('Firebase not initialized, cannot reset data');
    return false;
  }

  try {
    console.log('Resetting and seeding Firestore with test methodologies...');
    
    // Use batch write for better atomicity
    const batch = writeBatch(db);
    
    // Get existing methodologies to delete them
    const snapshot = await getDocs(collection(db, METHODOLOGIES_COLLECTION));
    
    // Delete existing documents
    snapshot.docs.forEach(document => {
      batch.delete(doc(db, METHODOLOGIES_COLLECTION, document.id));
    });
    
    // Add each methodology to the batch
    DEFAULT_METHODOLOGIES.forEach(methodology => {
      const docRef = doc(db, METHODOLOGIES_COLLECTION, methodology.id);
      // Convert string timestamps to server timestamps for new records
      batch.set(docRef, {
        ...methodology,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });
    
    // Commit the batch
    await batch.commit();
    
    console.log('Successfully reset and seeded Firestore with test methodologies');
    return true;
  } catch (error) {
    console.error('Error resetting Firestore:', error);
    return false;
  }
};

export const checkAndSeedFirestore = async (): Promise<void> => {
  if (!firebaseService.isInitialized || !db) {
    console.warn('Firebase not initialized, skipping seed check');
    return;
  }

  try {
    const methodologiesRef = collection(db, METHODOLOGIES_COLLECTION);
    const snapshot = await getDocs(methodologiesRef);
    
    if (snapshot.empty) {
      console.log('No methodologies found, seeding Firestore...');
      await seedFirestore();
    } else {
      console.log(`Found ${snapshot.size} existing methodologies`);
    }
  } catch (error) {
    console.error('Error checking Firestore seed status:', error);
  }
};